import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SideMenuService } from '@services/sidemenu.service';
import { UtilityService } from '@services/utility.service';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

interface GroupedScreenPermission {
  moduleId: number;
  moduleName: string;
  screens: any[];
}

@Component({
  selector: 'app-user-permissions-modal',
  templateUrl: './user-permissions-modal.component.html',
  styleUrls: ['./user-permissions-modal.component.scss']
})
export class UserPermissionsModalComponent implements OnInit {
  @ViewChild('userPermissionsModal') userPermissionsModal!: TemplateRef<any>;
  
  private modalRef!: NgbModalRef;
  public selectedRoleId: number = 0;
  public screenPermissions: any[] = [];
  private originalPermissions: any[] = [];
  loader: boolean = false;
  selectedRole: any = null;
  public groupedScreenPermissions: GroupedScreenPermission[] = [];

  constructor(
    private modalService: NgbModal,
    private _utilityService: UtilityService,
    private toastr: ToastrService,
    private sideMenu: SideMenuService
  ) { }

  ngOnInit(): void {
    // Initialization logic if needed
  }

  /**
   * Open the modal and fetch permissions for the given role
   */
  public openModal(roleId: number, role?: any): void {
    this.selectedRoleId = roleId;
    this.selectedRole = role;
    
    this.modalRef = this.modalService.open(this.userPermissionsModal, {
      size: 'xl',
      backdrop: 'static',
      keyboard: false,
      centered: true,
      // scrollable: true
    });

    this.fetchRolePermissions(roleId);
  }

  /**
   * Close the modal
   */
  public closeModal(): void {
    if (this.modalRef) {
      this.modalRef.dismiss();
    }
  }

  public fetchRolePermissions(id: number): void {
    this.screenPermissions = [];
    this.loader = true;
    
    this._utilityService.getRolePermissions(id).pipe(
      tap((response: any) => {
        if (response) {
          this.loader = false;
          this.extractChildPermissions(response);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        this.loader = false;
        if (err.error?.message) {
          this.toastr.error(err.error.message);
        } else {
          this.toastr.error('Sorry, looks like there are some errors detected, please try again.');
        }
        return of(undefined);
      })
    ).subscribe();
  }

  public toggleAllPermissions(permission: any): void {
    const isAllSelected = permission.canAdd && permission.canEdit && permission.canView && permission.canDelete;

    permission.canAdd = !isAllSelected;
    permission.canEdit = !isAllSelected;
    permission.canView = !isAllSelected;
    permission.canDelete = !isAllSelected;
  }

  public toggleViewPermission(permission: any): void {
    if (!permission.canView) {
      permission.canAdd = false;
      permission.canEdit = false;
      permission.canDelete = false;
    }
  }

  public updateRolePermissions(): void {
    // Validation: Check if at least one permission is selected
  if (!this.hasAtLeastOnePermission()) {
    this.toastr.warning('Please select at least one permission before saving.');
    return;
  }
    this.loader = true;

    const permissionsToUpdate = this.screenPermissions.map(permission => ({
      menu: permission.menuId,
      canAdd: permission.canAdd,
      canEdit: permission.canEdit,
      canView: permission.canView,
      canDelete: permission.canDelete
    })).filter((permission, index) => {
      const original = this.originalPermissions[index];
      return (
        permission.canAdd != original.canAdd ||
        permission.canEdit != original.canEdit ||
        permission.canView != original.canView ||
        permission.canDelete != original.canDelete
      );
    });

    if (permissionsToUpdate.length > 0) {
      const payload = {
        role: this.selectedRoleId,
        data: permissionsToUpdate
      };

      this._utilityService.updateRolePermissions(payload).pipe(
        tap(response => {
          if (response) {
            this.loader = false;
            this.toastr.success(response.message);
            this.fetchRolePermissions(this.selectedRoleId);
            this.sideMenu.triggerMenuUpdate();
            this.closeModal();
          }
        }),
        catchError((err: HttpErrorResponse) => {
          this.loader = false;
          this.handleApiFieldErrors(err, this.toastr);
          return of(undefined);
        })
      ).subscribe();
    } else {
      this.loader = false;
      this.toastr.info('No permissions to update.');
    }
  }

  private handleApiFieldErrors(error: HttpErrorResponse, toastr: ToastrService): void {
    // Case 1: status is an array
    if (error.error?.status && Array.isArray(error.error.status)) {
      error.error.status.forEach((msg: string) => {
        toastr.error(msg);
      });
      return;
    }

    // Case 2: single message
    if (error.error?.message) {
      toastr.error(error.error.message);
      return;
    }

    // Case 3: field validation errors
    if (error.error?.errors) {
      const errors = error.error.errors;
      Object.keys(errors).forEach(key => {
        const message = Array.isArray(errors[key]) ? errors[key][0] : errors[key];
        toastr.error(message);
      });
      return;
    }

    // Fallback
    toastr.error('Sorry, something went wrong. Please try again.');
  }

  private extractChildPermissions(modules: any[]): void {
    this.screenPermissions = [];
    this.originalPermissions = [];
    this.groupedScreenPermissions = [];

    const moduleMap = new Map<number, { name: string, screens: any[] }>();

    modules.forEach(module => {
      const itemsToProcess = module.children && module.children.length > 0 ? module.children : [module];

      itemsToProcess.forEach((item: {
        id: any; displayName: any; canAdd: any; canEdit: any; canView: any; canDelete: any; parentId?: any;
      }) => {
        if (!module.children.length && module.parentId === null && module.is_category || item) {
          const permission = {
            menuId: item.id,
            displayName: item.displayName,
            canAdd: item.canAdd,
            canEdit: item.canEdit,
            canView: item.canView,
            canDelete: item.canDelete
          };
          
          this.screenPermissions.push(permission);
          this.originalPermissions.push({ ...permission });

          const parentModuleId = module.id;
          const parentModuleName = module.displayName;

          if (!moduleMap.has(parentModuleId)) {
            moduleMap.set(parentModuleId, {
              name: parentModuleName,
              screens: []
            });
          }

          moduleMap.get(parentModuleId)!.screens.push(permission);
        }
      });
    });

    moduleMap.forEach((value, key) => {
      if (value.screens.length > 0) {
        this.groupedScreenPermissions.push({
          moduleId: key,
          moduleName: value.name,
          screens: value.screens
        });
      }
    });
  }

  // TrackBy functions for performance
  trackByModuleId(index: number, item: GroupedScreenPermission): number {
    return item.moduleId;
  }

  trackByMenuId(index: number, item: any): number {
    return item.menuId;
  }

  // Helper to check if all permissions are selected
  isAllPermissionsSelected(permission: any): boolean {
    return permission.canAdd && permission.canEdit && permission.canView && permission.canDelete;
  }

  // Handle view permission change with proper logic
  onViewPermissionChange(permission: any, value: boolean): void {
    permission.canView = value;
    this.toggleViewPermission(permission);
  }
  public isAllModulePermissionsSelected(group: GroupedScreenPermission): boolean {
  return group.screens.every(screen => 
    screen.canAdd && screen.canEdit && screen.canView && screen.canDelete
  );
}

public toggleAllModulePermissions(group: GroupedScreenPermission): void {
  const isAllSelected = this.isAllModulePermissionsSelected(group);
  
  group.screens.forEach(screen => {
    screen.canAdd = !isAllSelected;
    screen.canEdit = !isAllSelected;
    screen.canView = !isAllSelected;
    screen.canDelete = !isAllSelected;
  });
}
private hasAtLeastOnePermission(): boolean {
  return this.screenPermissions.some(permission => 
    permission.canView || permission.canAdd || permission.canEdit || permission.canDelete
  );
}
}